import type { AdapterFetchOptions, AdapterResult, MarketSourceAdapter } from '@/types/adapters';
import type { NormalizedMarketRow } from '@/types/market';
import { appConfig } from '@/config/app';
import { SOURCE_REGISTRY } from '@/data/sourceRegistry';

const EXPECTED_FIELDS = 2; // this file only has medianSalePrice (ZHVI) + its prior-period value -- see toRow() below

interface NeighborhoodMapRow {
  geoId: string;
  name: string;
  city: string;
  metro: string | null;
  county: string | null;
  periodLabel: string;
  publishedAt: string;
  zhvi: number;
  zhviPrevPeriod: number | null;
}

interface NeighborhoodMapResponse {
  source: string;
  asOf: string | null;
  publishedAt: string | null;
  fetchedAt: string;
  neighborhoodCount: number;
  neighborhoods: NeighborhoodMapRow[];
  warnings: string[];
}

async function fetchJson<T>(path: string, signal?: AbortSignal): Promise<T | null> {
  for (const base of appConfig.mayApiBaseUrlCandidates) {
    try {
      const res = await fetch(`${base}${path}`, { signal, mode: 'cors' });
      if (res.ok) return (await res.json()) as T;
    } catch {
      // try next candidate -- network error, CORS, or that base isn't up
    }
  }
  return null;
}

function toRow(n: NeighborhoodMapRow): NormalizedMarketRow {
  const filled = [n.zhvi, n.zhviPrevPeriod].filter((v) => v !== null && v !== undefined).length;

  return {
    geoLevel: 'neighborhood',
    geoId: n.geoId,
    geoName: `${n.name}, ${n.city}`,
    sourceId: 'zillow',
    sourceLabel: SOURCE_REGISTRY.zillow.label,
    periodLabel: n.periodLabel,
    publishedAt: n.publishedAt,
    retrievedAt: new Date().toISOString(),
    // ZHVI is a smoothed home-VALUE index, not a listing or sale price --
    // mapped onto medianSalePrice as the closest available proxy field on
    // NormalizedMarketRow, same "closest available field, not a literal
    // match" caveat mayBackendAdapter.ts documents for Realtor.com's
    // listing-price data. zhviPrevPeriod is the PRIOR MONTH's value, not a
    // year-ago value, so it's mapped to medianSalePricePrevPeriod (a
    // generic period-over-period field) -- deliberately NOT to
    // medianSalePriceYoyPct, which the scoring engine's benchmark treats as
    // a literal year-over-year percentage; mislabeling a MoM delta as YoY
    // would silently corrupt every neighborhood's coolingRisk/opportunity
    // scores. Every other NormalizedMarketRow field (days on market,
    // inventory, sale-to-list ratio, price cuts) is left undefined because
    // this Zillow file simply doesn't include them at neighborhood level --
    // shown as omitted in the UI rather than guessed at.
    medianSalePrice: n.zhvi,
    medianSalePricePrevPeriod: n.zhviPrevPeriod ?? undefined,
    fieldCompleteness: filled / EXPECTED_FIELDS,
  };
}

/**
 * Real, live data adapter for the neighborhood layer -- fetches
 * GET /api/stats/neighborhood-map from the MAY Assistant backend (which
 * itself pulls Zillow's public ~90MB neighborhood ZHVI CSV server-side, see
 * zillowNeighborhoodMapAdapter.js for the full source/license/join
 * details). Only ever produces 'neighborhood'-level rows for the four
 * target metro cores -- county/ZIP/city coverage still comes entirely from
 * mayBackendAdapter.ts's Realtor.com data. Gated behind
 * featureFlags.ENABLE_ZILLOW via SOURCE_REGISTRY.zillow.enabled.
 */
export const zillowAdapter: MarketSourceAdapter = {
  meta: SOURCE_REGISTRY.zillow,

  async fetch(options?: AdapterFetchOptions): Promise<AdapterResult> {
    if (!SOURCE_REGISTRY.zillow.enabled) {
      return { rows: [], warnings: [], isSampleData: false };
    }

    const warnings: string[] = [];
    const data = await fetchJson<NeighborhoodMapResponse>('/api/stats/neighborhood-map', options?.signal);

    if (!data) {
      warnings.push('zillow: could not reach the backend at any configured API base URL -- no neighborhood data this refresh.');
      return { rows: [], warnings, isSampleData: false };
    }

    warnings.push(...(data.warnings || []).map((w) => `zillow: ${w}`));
    return { rows: data.neighborhoods.map(toRow), warnings, isSampleData: false };
  },
};
